import { useMemo } from 'react';
import { css } from '@emotion/react';
import { NoShowRateByType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { AnalyticsHooks, DemoChip, InfoTipPopover } from '@frontend/analytics-shared';
import { CategoryBarChartData, Chart } from '@frontend/charts';
import { i18next, useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { LocationBenchmarkDifference, type LocationNSRData } from '../components/location-benchmark-difference';
import { useFetchNSROILocationsNoShowRate } from '../hooks/use-fetch-nsr-locations-no-show-rate';
import { useNSRRoiShallowStore } from '../hooks/use-nsr-roi-store';
import { commonMultiAppearance } from './common-values';
import { CommonChartProps } from './types';

type ProcessedData = {
  chartData: CategoryBarChartData;
  highestNSRLocation: LocationNSRData;
  lowestNSRLocation: LocationNSRData;
};

const labels = {
  groupNameHeader: i18next.t('Location', { ns: 'analytics' }),
  yourPractice: i18next.t('No-Show Rate', { ns: 'analytics' }),
};

const colors = {
  yourPractice: theme.colors.secondary.seaweed30,
};

const strokeColors = {
  yourPractice: theme.colors.secondary.seaweed40,
};

export const NSRRoiLocationsComparisonBenchmark = ({ isDemoAccount }: CommonChartProps) => {
  const { t } = useTranslation('analytics');
  const { filters, setFilters } = useNSRRoiShallowStore('filters', 'setFilters');
  const { locationNames } = AnalyticsHooks.useAnalyticsOrgLocations({
    module: 'ROI',
  });

  const { data, isLoading } = useFetchNSROILocationsNoShowRate({
    noShowRateType: NoShowRateByType.WEAVE_AVERAGE,
    filters,
  });

  const processedData: ProcessedData = useMemo(() => {
    const lowestNSRLocation: LocationNSRData = {};
    const highestNSRLocation: LocationNSRData = {};

    const chartData: CategoryBarChartData =
      data?.data?.locations?.reduce<CategoryBarChartData>((acc, { locationId = '', noShowRate = 0 }) => {
        if (noShowRate < (lowestNSRLocation.noShowRate || Infinity)) {
          lowestNSRLocation.locationId = locationId;
          lowestNSRLocation.noShowRate = noShowRate;
        }

        if (noShowRate > (highestNSRLocation.noShowRate || -Infinity)) {
          highestNSRLocation.locationId = locationId;
          highestNSRLocation.noShowRate = noShowRate;
        }

        return {
          ...acc,
          [locationId]: {
            yourPractice: noShowRate,
          },
        };
      }, {}) || {};

    return {
      chartData,
      highestNSRLocation: {
        ...highestNSRLocation,
        diffFromBenchmark: (highestNSRLocation.noShowRate ?? 0) - (data?.data?.unsubscribed ?? 0) * 100,
      },
      lowestNSRLocation: {
        ...lowestNSRLocation,
        diffFromBenchmark: (lowestNSRLocation.noShowRate ?? 0) - (data?.data?.unsubscribed ?? 0) * 100,
      },
    };
  }, [data?.data?.locations, data?.data?.unsubscribed]);

  return (
    <Chart
      colors={colors}
      isLoading={isLoading}
      labels={{ ...labels, ...locationNames }}
      strokeColors={strokeColors}
      style={{ marginBottom: 0 }}
    >
      <Chart.Header
        title={t('No-Show Rate')}
        infoTip={
          <InfoTipPopover>
            {t(
              'A detailed breakdown of no-show rates across all locations, allowing practices to identify which location has the highest and lowest no-show rates.'
            )}
          </InfoTipPopover>
        }
        leftElement={isDemoAccount && <DemoChip />}
        removeHeaderBottomMargin
      />
      <div css={styles.locationBenchmarkDifference}>
        <LocationBenchmarkDifference isLowestNSRLocation locationNSRData={processedData.lowestNSRLocation} />
        <LocationBenchmarkDifference locationNSRData={processedData.highestNSRLocation} />
      </div>
      <Chart.CategoryBarChart
        appearance={{
          ...commonMultiAppearance(filters),
          customCategoryNameRenderer: (category) => (category === 'yourPractice' ? t('No-Show Rate') : undefined),
          formatters: {
            yourPractice: formatters.appendPercent,
          },
          maxValues: {
            yourPractice: 100,
          },
        }}
        data={processedData.chartData}
        highestValueConfig={{
          categoryId: 'yourPractice',
          color: theme.font.colors.error,
          iconName: 'alert-small',
          text: t('Highest no-show rate'),
        }}
        onClick={({ group }) => {
          setFilters({
            locationIds: [group],
          });
        }}
        sortConfig={{
          sortedBy: 'yourPractice',
        }}
      />
    </Chart>
  );
};

const styles = {
  locationBenchmarkDifference: css`
    display: flex;
    gap: ${theme.spacing(2)};
    flex-wrap: wrap;
    margin-bottom: ${theme.spacing(3)};
  `,
};
